import { useScriptTag } from '@vueuse/core';

import { useRuntimeConfigKey, type UseRuntimeConfig } from '@/injectionKeys';
import { PAYJP_URL } from '@/wabunka-src/constants/payjpUrl';
import { buildPaymentCardToken, type PaymentCardTokenResponse } from '@/wabunka-src/features/Experience';

/**
 * payjpのインスタンス初期化が一度しかできないので、getPayjpInstanceという関数で呼び出す
 *
 * `getPayjpInstance`は、以下のように、scriptでPayjpのライブラリを読み込んだ後に実行してください。
 * ```
 * await root.$loadScript('https://js.pay.jp/v2/pay.js');
 * ```
 */
let payjpInstance: Payjp | undefined;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let numberElement: any | undefined;

export const getPayjpInstance = (publicKey: string): Payjp => {
  if (payjpInstance) return payjpInstance;
  if (!window.Payjp) {
    throw new Error('unload payjp script');
  }
  const options = {
    locale: 'en',
  };
  payjpInstance = window.Payjp(publicKey, options);
  return payjpInstance;
};

export const usePayjp = () => {
  const useRuntimeConfig = inject(useRuntimeConfigKey) as UseRuntimeConfig;
  const runtimeConfig = useRuntimeConfig();
  const publicKey = runtimeConfig.public.WABUNKA_PAYJP_PUBLIC_KEY;

  const mountPayjpForm = () => {
    // DOM要素の存在確認（v-ifによる条件レンダリングとのタイミング競合を防止）
    const mountSelectors = ['#number-form', '#expiry-form', '#cvc-form'] as const;
    const missing = mountSelectors.filter((s) => !document.querySelector(s));
    if (missing.length > 0) {
      throw new Error(`Payment form elements not found in DOM: ${missing.join(', ')}`);
    }

    getPayjpInstance(publicKey);
    const style = {
      base: {
        fontFamily: 'Spectral, serif',
        fontSize: '16px',
        fontWeight: '300',
        // eslint-disable-next-line @typescript-eslint/naming-convention
        '::placeholder': {
          fontFamily: 'Spectral, serif',
          fontSize: '16px',
          fontWeight: '300',
          color: '#999',
        },
      },
      invalid: {
        color: '#C01F0E',
      },
    };
    const elements = payjpInstance.elements({ locale: 'en' });

    numberElement = elements.create('cardNumber', {
      style: style,
      placeholder: '1234 ****',
    });
    const expiryElement = elements.create('cardExpiry', { style });
    const cvcElement = elements.create('cardCvc', { style });

    numberElement.mount('#number-form');
    expiryElement.mount('#expiry-form');
    cvcElement.mount('#cvc-form');
  };

  const createPayjpToken = async (): Promise<PaymentCardTokenResponse> => {
    if (!payjpInstance || !numberElement) {
      return {
        data: undefined,
        error: {
          messages: ['PayJP instance or card element is not initialized.'],
        },
      };
    }

    try {
      const res = await payjpInstance.createToken(numberElement);
      if (res.error) {
        return {
          data: undefined,
          error: {
            messages: [res.error.message],
          },
        };
      }
      const cardToken = res.id;
      const cardLastFourDigits = res.card.last4;

      return {
        data: buildPaymentCardToken(cardToken, cardLastFourDigits),
      };
    } catch (error) {
      return {
        data: undefined,
        error: {
          messages: ['Unknown error occurred.'],
        },
      };
    }
  };

  const { load: loadPayjpScript, unload: unLoadPayjpPaymentForm } = useScriptTag(PAYJP_URL, () => {}, { manual: true });

  // スクリプト読み込み後にマウントを実行する
  // onLoadedコールバック内でのエラーはunhandled rejectionになるため、
  // load()のPromise解決後に同期的にmountPayjpFormを呼び出す
  const loadPayjpPaymentForm = async (waitForScriptLoad?: boolean) => {
    await loadPayjpScript(waitForScriptLoad);
    if (!window.Payjp) {
      throw new Error(`Payjp script loaded but window.Payjp is undefined (waitForScriptLoad=${waitForScriptLoad})`);
    }
    mountPayjpForm();
  };

  /**
   * 最低決済可能金額を下回っているかどうか？
   */
  const isBelowPayjpMinimumPaymentPrice = (paymentPrice: number) => {
    return paymentPrice < 50;
  };

  return {
    createPayjpToken,
    loadPayjpPaymentForm,
    unLoadPayjpPaymentForm,
    isBelowPayjpMinimumPaymentPrice,
  };
};
